/**
 * Typed client for the Pages Functions API. The browser only ever
 * speaks to these endpoints — never directly to Analytics Engine.
 *
 * v1 shapes live in `@/types/v1` and match exactly what the functions
 * return today; the richer aspirational types in `@/types` are for the
 * eventual feature-complete API surface.
 */
import type { FilterState } from "@/types";
import type {
  ApiEnvelopeV1,
  CampaignV1,
  CommonPivotV1,
  CountryActivityV1,
  DoHealthV1,
  EndpointMatrixV1,
  EntityDetailV1,
  EntityTypeV1,
  EventsPageV1,
  GlobalActivityMetricV1,
  MetaV1,
  ReasonCooccurrenceV1,
  RepeatOffendersV1,
  ScoreHistogramV1Bucket,
  SummaryV1,
  TargetedAccountsV1,
  ThreatListLiftV1Row,
  TimeseriesPointV1,
  TopJa4ByIpsV1Row,
  TrendsV1,
  UnderAttackModeV1,
  UnderAttackSeedInputV1,
} from "@/types/v1";

export class ApiCallError extends Error {
  status: number;
  details?: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

function buildQuery(filters: Partial<FilterState>, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v == null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      p.set(k, v.join(","));
    } else if (typeof v === "boolean") {
      p.set(k, v ? "true" : "false");
    } else {
      p.set(k, String(v));
    }
  }
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

async function call<T>(path: string, filters: Partial<FilterState> = {}, extra: Record<string, string> = {}, signal?: AbortSignal): Promise<ApiEnvelopeV1<T>> {
  const res = await fetch(`/api/${path}${buildQuery(filters, extra)}`, {
    headers: { accept: "application/json" },
    signal,
  });
  let body: ApiEnvelopeV1<T> | undefined;
  try { body = await res.json() as ApiEnvelopeV1<T>; } catch { /* ignore JSON parse errors */ }
  if (!res.ok || body?.error) {
    throw new ApiCallError(body?.error?.message || `API ${res.status}`, res.status, body?.error);
  }
  if (!body) throw new ApiCallError("Empty response", res.status);
  return body;
}

async function post<T>(path: string, filters: Partial<FilterState>, bodyData: unknown, signal?: AbortSignal): Promise<ApiEnvelopeV1<T>> {
  const res = await fetch(`/api/${path}${buildQuery(filters)}`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(bodyData),
    signal,
  });
  let body: ApiEnvelopeV1<T> | undefined;
  try { body = await res.json() as ApiEnvelopeV1<T>; } catch { /* ignore JSON parse errors */ }
  if (!res.ok || body?.error) {
    throw new ApiCallError(body?.error?.message || `API ${res.status}`, res.status, body?.error);
  }
  if (!body) throw new ApiCallError("Empty response", res.status);
  return body;
}

export const api = {
  meta: (signal?: AbortSignal) => call<MetaV1>("meta", {}, {}, signal),
  summary: (f: Partial<FilterState>, signal?: AbortSignal) => call<SummaryV1>("summary", f, {}, signal),
  timeseries: (f: Partial<FilterState>, signal?: AbortSignal) => call<TimeseriesPointV1[]>("timeseries", f, {}, signal),
  globalActivity: (f: Partial<FilterState>, metric: GlobalActivityMetricV1, signal?: AbortSignal) =>
    call<CountryActivityV1[]>("global-activity", f, { metric }, signal),
  doHealth: (f: Partial<FilterState>, signal?: AbortSignal) => call<DoHealthV1>("do-health", f, {}, signal),

  events: (f: Partial<FilterState>, opts: { page?: number; pageSize?: number; sort?: string; sortDir?: "asc" | "desc" } = {}, signal?: AbortSignal) =>
    call<EventsPageV1>("events", f, {
      page: String(opts.page ?? 1),
      pageSize: String(opts.pageSize ?? 50),
      ...(opts.sort ? { sort: opts.sort } : {}),
      ...(opts.sortDir ? { sortDir: opts.sortDir } : {}),
    }, signal),

  entityDetail: (f: Partial<FilterState>, type: EntityTypeV1, value: string, signal?: AbortSignal) =>
    call<EntityDetailV1>("entity-detail", f, { type, value }, signal),

  campaigns: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<CampaignV1[]>("campaigns", f, {}, signal),

  trends: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<TrendsV1>("trends", f, {}, signal),

  commonPivot: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<CommonPivotV1>("common-pivot", f, {}, signal),

  targetedAccounts: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<TargetedAccountsV1>("targeted-accounts", f, {}, signal),

  threatListLift: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<ThreatListLiftV1Row[]>("threat-list-lift", f, {}, signal),

  topJa4ByIps: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<TopJa4ByIpsV1Row[]>("top-ja4-by-ips", f, {}, signal),

  scoreHistogram: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<ScoreHistogramV1Bucket[]>("score-histogram", f, {}, signal),

  reasonCooccurrence: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<ReasonCooccurrenceV1>("reason-cooccurrence", f, {}, signal),

  repeatOffenders: (f: Partial<FilterState>, signal?: AbortSignal) =>
    call<RepeatOffendersV1>("repeat-offenders", f, {}, signal),

  endpointMatrix: (f: Partial<FilterState>, dim: "asn" | "country", signal?: AbortSignal) =>
    call<EndpointMatrixV1>("endpoint-matrix", f, { dim }, signal),

  underAttackMode: (f: Partial<FilterState>, seeds: UnderAttackSeedInputV1, signal?: AbortSignal) =>
    post<UnderAttackModeV1>("under-attack-mode", f, { seeds }, signal),
};
